import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useWindowHeight, useWindowWidth } from '@react-hook/window-size';
import { isMobile, isMobileSafari } from 'react-device-detect';
import { useSelector } from 'react-redux';
import { Carousel } from 'react-responsive-carousel';
import ScrollContainer from 'react-indiana-drag-scroll';
import { getFontClassName, getFontSize, getSpecificationWidth } from './../../styleFunctions';
import {
  getLanguageInfo,
  getCarInfo,
  getCarGalleryInfo,
  gtmGalleryInteraction,
  gtmShowroomGalleryInteraction,
  gtmJourneyEventWithCustomFields,
  gtmJourneyEvent,
} from './../../common';
import { ImageComp } from './image';
import IframeComp from './iframe';
import VideoComp from './video';
import { CustomModal } from './modal';
import { useResponsiveSize } from './hooks';
import localization from './../../data/localization.json';

export const ImageCarousel = ({
  slideIdx,
  indexChange,
  imageWidth,
  swipeable,
  slideImages = [],
  rtl = false,
  opened = true,
}) => {
  const windowWidth = useWindowWidth();
  const windowHeight = useWindowHeight();
  const getResponsiveSize = useResponsiveSize();
  const arrowStyles = {
    position: 'fixed',
    zIndex: 102,
    top:
      windowWidth > windowHeight
        ? (imageWidth * 0.541) / 2 -
          getResponsiveSize(16, 16) +
          getResponsiveSize(88, 80) +
          getResponsiveSize(84, 33) +
          getResponsiveSize(40, 16) +
          getResponsiveSize(24, 24) // imageHeight + titleHeight + indicatorHeight + menuPaddingTop + closeHeight
        : windowHeight * 0.5 +
          ((imageWidth * 0.541) / 2 - getResponsiveSize(16, 16)) +
          getResponsiveSize(84, 54) +
          getResponsiveSize(88, 80), // blankHeight + imageHeight + indicatorHeight + titleHeight
    width: getResponsiveSize(32, 32),
    height: getResponsiveSize(32, 32),
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: 0,
    margin: 0,
  };

  return (
    <Carousel
      selectedItem={slideIdx}
      onChange={indexChange}
      showThumbs={false}
      showStatus={false}
      showIndicators={false}
      useKeyboardArrows
      emulateTouch
      infiniteLoop
      swipeable={swipeable}
      preventMovementUntilSwipeScrollTolerance={true}
      swipeScrollTolerance={50}
      autoPlay={false}
      interval={300000000}
      className="carousel-style-class"
      renderArrowPrev={(onClickHandler, hasPrev, label) =>
        opened ? (
          <button
            type="button"
            onClick={onClickHandler}
            title={label}
            style={{
              ...arrowStyles,
              left:
                windowWidth > windowHeight
                  ? rtl
                    ? getResponsiveSize(8, 8)
                    : windowWidth - getSpecificationWidth(windowWidth, windowHeight) + getResponsiveSize(8, 8)
                  : getResponsiveSize(8, 8),
              cursor: 'pointer',
            }}
          >
            <ImageComp
              src={'/static/UI/btn-features-arrow.png'}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </button>
        ) : null
      }
      renderArrowNext={(onClickHandler, hasNext, label) =>
        opened ? (
          <button
            type="button"
            onClick={onClickHandler}
            title={label}
            style={{
              ...arrowStyles,
              right:
                windowWidth > windowHeight
                  ? !rtl
                    ? getResponsiveSize(8, 8)
                    : windowWidth - getSpecificationWidth(windowWidth, windowHeight) + getResponsiveSize(8, 8)
                  : getResponsiveSize(8, 8),
              cursor: 'pointer',
            }}
          >
            <ImageComp
              src={'/static/UI/btn-features-arrow.png'}
              style={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'rotate(180deg)' }}
            />
          </button>
        ) : null
      }
    >
      {slideImages}
    </Carousel>
  );
};

export const InfoTextDiv = ({ color = 'white', zIndex = 5, left = 0, right = 0, bottom = 0 }) => {
  const language = useSelector(({ languageReducer }) => languageReducer.language);
  const windowWidth = useWindowWidth();
  const windowHeight = useWindowHeight();

  const bottomPosition = useMemo(() => {
    if (isMobile) {
      if (windowWidth > windowHeight) {
        return 18;
      } else {
        return (bottom === null ? 0 : bottom) + 32;
      }
    } else {
      return 36;
    }
  }, [bottom]);

  const sidePosition = useMemo(() => {
    if (isMobile) {
      if (windowHeight < windowWidth) {
        return {
          left: left + windowWidth * 0.15,
          right: right + windowWidth * 0.15,
        };
      } else {
        return {
          left: 10,
          right: 10,
        };
      }
    } else {
      return {
        left: left + 10,
        right: right + 10,
      };
    }
  }, [left, right]);

  return (
    <div
      className={getFontClassName('light', language)}
      style={{
        position: 'fixed',
        left: sidePosition.left,
        right: sidePosition.right,
        bottom: bottomPosition,
        zIndex: zIndex,
        color: color,
        fontSize: getFontSize(windowHeight * 0.017, language),
        textAlign: 'center',
        verticalAlign: 'middle',
        direction: getLanguageInfo(language).rtl ? 'rtl' : 'ltr',
      }}
    >
      {language !== null ? localization['loading_txt_0'][language] : ''}
    </div>
  );
};

export const FullScreenImageCarousel = ({ carId, closeFunc, fromShowroom = false }) => {
  const [slideIdx, setSlideIdx] = useState(0);
  const [showThumbs, setShowThumbs] = useState(false);
  const [clickedRotate, setClickedRotate] = useState(false);

  const windowWidth = useWindowWidth();
  const windowHeight = useWindowHeight();
  const getResponsiveSize = useResponsiveSize();
  const language = useSelector(({ languageReducer }) => languageReducer.language);
  const thumbLineRef = useRef(null);

  const changeSlideIdx = (idx) => {
    setSlideIdx(idx);
  };
  const arrowStyles = {
    position: 'absolute',
    zIndex: 2,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    padding: 0,
    margin: 0,
  };
  const thumbSize = { width: getResponsiveSize(156, 156), height: getResponsiveSize(96, 96) };
  const getThumbMarginLeft = (slideIdx) => {
    let slideCount = getCarGalleryInfo(carId).gallery.length;
    let actualLength = slideCount * (thumbSize.width + getResponsiveSize(16, 16));
    let containerWidth = windowWidth - getResponsiveSize(20, 20) * 2;
    if (actualLength <= containerWidth) {
      return getResponsiveSize(8, 8);
    } else {
      let necessarySlides = Math.floor(containerWidth / (thumbSize.width + getResponsiveSize(16, 16)));
      if (slideIdx + 1 <= necessarySlides) {
        return getResponsiveSize(8, 8);
      } else if (slideCount - slideIdx <= necessarySlides) {
        return actualLength - containerWidth;
      } else {
        return (thumbSize.width + getResponsiveSize(16, 16)) * (slideIdx - necessarySlides / 2);
      }
    }
  };

  useEffect(() => {
    if (window.slideScroll) {
      gtmGalleryInteraction(`Impressions|scroll`, getCarInfo(carId).brand);
    }
    window.slideScroll = true;
  }, [slideIdx, carId]);

  // useEffect(() => {
  //     window.ignoreGalleryThumbScrollEvent = false
  // }, [])

  return (
    <>
      {!isMobile || windowWidth > windowHeight || clickedRotate ? (
        <div
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            right: 0,
            backgroundColor: 'black',
            zIndex: 20,
          }}
        >
          <Carousel
            selectedItem={slideIdx}
            onChange={(value) => {
              changeSlideIdx(value);
              thumbLineRef.current.getElement().scrollTo(getThumbMarginLeft(value), 0);
            }}
            showThumbs={false}
            showStatus={false}
            showIndicators={false}
            useKeyboardArrows
            emulateTouch
            swipeable
            infiniteLoop
            className="carousel-style-class"
            renderArrowPrev={(onClickHandler, hasPrev, label) => (
              <button
                type="button"
                onClick={() => {
                  changeSlideIdx(slideIdx === 0 ? getCarGalleryInfo(carId).gallery.length - 1 : slideIdx - 1);
                  // window.ignoreGalleryThumbScrollEvent = true
                  thumbLineRef.current
                    .getElement()
                    .scrollTo(
                      getThumbMarginLeft(slideIdx === 0 ? getCarGalleryInfo(carId).gallery.length - 1 : slideIdx - 1),
                      0,
                    );
                  gtmGalleryInteraction(`Click|image left`, getCarInfo(carId).brand);
                  // setTimeout(() => {
                  //     window.ignoreGalleryThumbScrollEvent = false
                  // }, 1000)
                }}
                title={label}
                style={{
                  ...arrowStyles,
                  width: getResponsiveSize(23, 23),
                  height: getResponsiveSize(38, 38),
                  top: windowHeight / 2 - getResponsiveSize(19, 19),
                  left: getResponsiveSize(16, 16),
                  cursor: 'pointer',
                }}
              >
                <ImageComp
                  src={'/static/UI/btn-arrow.png'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'rotate(180deg)' }}
                />
              </button>
            )}
            renderArrowNext={(onClickHandler, hasNext, label) => (
              <button
                type="button"
                onClick={() => {
                  changeSlideIdx(slideIdx === getCarGalleryInfo(carId).gallery.length - 1 ? 0 : slideIdx + 1);
                  // window.ignoreGalleryThumbScrollEvent = true
                  thumbLineRef.current
                    .getElement()
                    .scrollTo(
                      getThumbMarginLeft(slideIdx === getCarGalleryInfo(carId).gallery.length - 1 ? 0 : slideIdx + 1),
                      0,
                    );
                  gtmGalleryInteraction(`Click|image right`, getCarInfo(carId).brand);
                  // setTimeout(() => {
                  //     window.ignoreGalleryThumbScrollEvent = false
                  // }, 1000)
                }}
                title={label}
                style={{
                  ...arrowStyles,
                  width: getResponsiveSize(23, 23),
                  height: getResponsiveSize(38, 38),
                  top: windowHeight / 2 - getResponsiveSize(19, 19),
                  right: getResponsiveSize(16, 16),
                  cursor: 'pointer',
                }}
              >
                <ImageComp
                  src={'/static/UI/btn-arrow.png'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </button>
            )}
          >
            {getCarGalleryInfo(carId).gallery.map((ele, idx) => (
              <ImageComp
                key={idx}
                src={ele.imgUrl}
                alt={`Slide ${idx}`}
                style={{
                  width: windowWidth,
                  height: windowHeight,
                  objectFit: windowWidth > windowHeight ? 'cover' : 'contain',
                  background: 'black',
                }}
              />
            ))}
          </Carousel>
          <ImageComp
            src={'/static/UI/btn-close.png'}
            style={{
              ...arrowStyles,
              cursor: 'pointer',
              width: getResponsiveSize(29, 29),
              height: getResponsiveSize(29, 29),
              [getLanguageInfo(language).rtl ? 'left' : 'right']: getResponsiveSize(16, 16),
              top: getResponsiveSize(18, 18),
              zIndex: 2,
            }}
            onClick={() => {
              if (fromShowroom) {
                gtmShowroomGalleryInteraction('Click|gallery close', getCarInfo(carId).brand);
              } else {
                gtmGalleryInteraction(`Click|close`, getCarInfo(carId).brand);
              }
              closeFunc();
            }}
          />
          <ImageComp
            src={'/static/UI/btn-arrow.png'}
            style={{
              ...arrowStyles,
              cursor: 'pointer',
              height: getResponsiveSize(38, 38),
              width: getResponsiveSize(38, 38),
              top: 'unset',
              [getLanguageInfo(language).rtl ? 'left' : 'right']: getResponsiveSize(16, 16),
              bottom: showThumbs ? thumbSize.height + getResponsiveSize(38, 38) : getResponsiveSize(18, 18),
              transform: showThumbs ? 'rotate(90deg)' : 'rotate(-90deg)',
              transitionProperty: 'transform, bottom',
              transitionDuration: '0.5s',
              zIndex: 12,
              objectFit: 'contain',
            }}
            onClick={() => {
              gtmGalleryInteraction(`Click|gallery ${showThumbs ? 'hide' : 'show'}`, getCarInfo(carId).brand);
              window.slideScroll = true;
              setShowThumbs(!showThumbs);
            }}
          />
          {/* thumb gallery */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: showThumbs ? getResponsiveSize(20, 20) : -getResponsiveSize(20, 20) - thumbSize.height,
              height: thumbSize.height,
              backgroundColor: 'transparent',
              transitionProperty: 'bottom',
              transitionDuration: '0.5s',
              zIndex: 3,
            }}
          >
            <div
              className={getFontClassName('light', language)}
              style={{
                color: '#fff',
                paddingLeft: getResponsiveSize(32, 32),
                paddingRight: getResponsiveSize(72, 72),
                marginTop: -getResponsiveSize(74, 74),
                fontSize: getResponsiveSize(20, 20),
                height: getResponsiveSize(74, 74),
                marginBottom: 0,
                textShadow: '#000 0px 0 5px',
              }}
            >
              {getCarGalleryInfo(carId).gallery[slideIdx].disclaimer
                ? getCarGalleryInfo(carId).gallery[slideIdx].disclaimer[language]
                : null}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
              <div style={{ width: getResponsiveSize(22, 22) }}></div>
              <ScrollContainer
                ref={thumbLineRef}
                className="thumb-line-scroll-bar"
                style={{
                  flex: 1,
                  height: '100%',
                  overflow: 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                }}
                // onScroll={() => {
                //     if (window.ignoreGalleryThumbScrollEvent === false) {
                //         let scrollLeftValue = Math.round(thumbLineRef.current.getElement().scrollLeft / (thumbSize.width + getResponsiveSize(16, 16)))
                //         setSlideIdx(scrollLeftValue)
                //     }
                // }}
              >
                {getCarGalleryInfo(carId).gallery.map((ele, key) => (
                  <ImageComp
                    key={`thumb-${key}`}
                    src={ele.thumbUrl}
                    style={{
                      height: slideIdx === key ? thumbSize.height - 2 : thumbSize.height,
                      minWidth: slideIdx === key ? thumbSize.width - 2 : thumbSize.width,
                      maxWidth: slideIdx === key ? thumbSize.width - 2 : thumbSize.width,
                      margin: `0px ${getResponsiveSize(8, 8)}px`,
                      objectFit: 'cover',
                      border: slideIdx === key ? `1px solid white` : 'none',
                      cursor: 'pointer',
                      transitionProperty: 'margin',
                      transitionDuration: '0.5s',
                    }}
                    onClick={() => {
                      window.slideScroll = false;
                      changeSlideIdx(key);
                      // window.ignoreGalleryThumbScrollEvent = true
                      thumbLineRef.current.getElement().scrollTo(getThumbMarginLeft(key), 0);
                      // setTimeout(() => {
                      //     window.ignoreGalleryThumbScrollEvent = false
                      // }, 1000)
                    }}
                  />
                ))}
              </ScrollContainer>
              <div style={{ width: getResponsiveSize(22, 22) }}></div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <ImageComp
            src={'/static/UI/btn-close.png'}
            style={{
              ...arrowStyles,
              cursor: 'pointer',
              width: getResponsiveSize(29, 29),
              height: getResponsiveSize(29, 29),
              [getLanguageInfo(language).rtl ? 'left' : 'right']: getResponsiveSize(16, 16),
              top: getResponsiveSize(18, 18),
              zIndex: 25,
            }}
            onClick={() => {
              if (fromShowroom) {
                gtmShowroomGalleryInteraction('Click|gallery close', getCarInfo(carId).brand);
              } else {
                gtmGalleryInteraction(`Click|close`, getCarInfo(carId).brand);
              }
              closeFunc();
            }}
          />
          <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, right: 0, background: 'black', zIndex: 20 }}>
            <ImageComp
              src={getCarGalleryInfo(carId).gallery[slideIdx].imgUrl}
              style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0.7 }}
            />
          </div>
          <div
            style={{
              position: 'fixed',
              left: 0,
              top: 0,
              bottom: 0,
              right: 0,
              background: 'transparent',
              zIndex: 21,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              setClickedRotate(true);
            }}
          >
            <ImageComp src={'/static/UI/btn-rotate.png'} style={{ width: 67, height: 67 }} />
            <div
              className={getFontClassName('bold', language)}
              style={{ fontSize: 12, color: 'white', marginTop: -24, marginBottom: 27 }}
            >
              {localization['device_rotate_txt'][language]}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export const MultimediaGallery = ({
  opened = false,
  title = '',
  closeFunc = () => {},
  carCode = null,
  medias = [],
  languageInfo = null,
}) => {
  const [popupOpacity, setPopupOpacity] = useState(0);
  const [scrollBottomButtonShowed, setShowScrollBottomButton] = useState(false);
  const windowHeight = useWindowHeight();
  const getResponsiveSize = useResponsiveSize();
  const scrollRef = useRef(null);
  useEffect(() => {
    if (opened) {
      setTimeout(() => setPopupOpacity(1), 200);
    }
  }, [opened]);

  useEffect(() => {
    if (opened) {
      if (scrollRef.current !== null) {
        let scrollHeight = scrollRef.current.scrollHeight;
        let eleHeight = scrollRef.current.clientHeight;
        setShowScrollBottomButton(scrollHeight > eleHeight);
      }
    }
  }, [opened, scrollRef, windowHeight, setShowScrollBottomButton]);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          display: opened ? 'block' : 'none',
          opacity: popupOpacity,
          transitionProperty: 'opacity',
          transitionDuration: '0.2s',
          transitionTimingFunction: 'ease-out',
          left: 0,
          top: -360,
          bottom: -360,
          right: 0,
          zIndex: 21,
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div style={{ position: 'absolute', top: 360, bottom: 360, left: 0, right: 0, backgroundColor: '#08141e' }}>
            <ImageComp
              src={'/static/UI/btn-close.png'}
              style={{
                cursor: 'pointer',
                position: 'fixed',
                zIndex: 21,
                top: getResponsiveSize(47, 24.5),
                left: languageInfo.rtl ? getResponsiveSize(47, 44.5) : 'unset',
                right: languageInfo.rtl ? 'unset' : getResponsiveSize(47, 44.5),
                width: getResponsiveSize(34, 29),
                height: getResponsiveSize(34, 29),
              }}
              onClick={() => {
                setPopupOpacity(0);
                setTimeout(closeFunc, 300);
              }}
            />
            <div
              className={languageInfo.id ? getFontClassName('bold', languageInfo.id) : ''}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                paddingTop: getResponsiveSize(136, 115),
                paddingBottom: getResponsiveSize(60, 38),
                direction: languageInfo.rtl ? 'rtl' : 'ltr',
                fontSize: getResponsiveSize(32, 32),
                textAlign: 'center',
                color: 'white',
              }}
            >
              {title}
            </div>
            <div
              ref={scrollRef}
              style={{
                marginLeft: 'auto',
                marginRight: 'auto',
                width: `calc(100% - ${getResponsiveSize(140, 140)}px)`,
                maxWidth: isMobile ? 'unset' : getResponsiveSize(1300, 1300),
                height: `calc(100% - ${getResponsiveSize(360, 180)}px)`,
                overflowX: 'visible',
                overflowY: 'auto',
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: languageInfo.rtl ? 'row-reverse' : 'row',
                justifyContent: 'flex-start',
              }}
            >
              {medias.map((value, idx) => (
                <div
                  key={`media-${idx}`}
                  style={{
                    cursor: value.media === null ? 'not-allowed' : 'pointer',
                    width: isMobile ? '100%' : getResponsiveSize(400, 400),
                    marginRight: isMobile || idx % 3 === 2 ? 0 : getResponsiveSize(38, 38),
                    marginBottom: getResponsiveSize(80, 40),
                  }}
                  onClick={() => {
                    if (value.media !== null) {
                      window.open(value.media, '_blank');
                    }
                  }}
                >
                  <ImageComp
                    src={value.thumb}
                    style={{ width: `100%`, height: isMobile ? 'auto' : getResponsiveSize(225, 225) }}
                    alt={`media ${idx}`}
                  />
                  <div
                    className={languageInfo.id ? `${getFontClassName('light', languageInfo.id)}` : ''}
                    style={{
                      direction: languageInfo.rtl ? 'rtl' : 'ltr',
                      marginTop: getResponsiveSize(19, 20),
                      width: `100%`,
                      fontSize: getResponsiveSize(24, 23),
                      color: 'white',
                    }}
                  >
                    {languageInfo.id ? value[languageInfo.id] : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {scrollBottomButtonShowed ? (
            <ImageComp
              src={'/static/UI/btn-dark-arrow.png'}
              style={{
                position: 'fixed',
                width: getResponsiveSize(50, 50),
                height: getResponsiveSize(50, 50),
                bottom: getResponsiveSize(28, 28),
                [languageInfo !== null && languageInfo.rtl ? 'left' : 'right']: getResponsiveSize(34, 34),
                cursor: 'pointer',
              }}
              onClick={() => {
                scrollRef.current.scrollBy(0, 1000000);
              }}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

const checkIfMp4 = (url) => {
  let urlparts = url.split('.');
  let extension = urlparts[urlparts.length - 1];
  if (extension.toLowerCase() === 'mp4') return true;
  return false;
};

export const UspModal = ({ opened = false, closeFunc = () => {}, uspUrl, languageInfo = null }) => {
  const [popupOpacity, setPopupOpacity] = useState(0);
  const [replayShow, setReplayShow] = useState(false);
  const mainRef = useRef(null);
  useEffect(() => {
    if (opened) {
      setTimeout(() => setPopupOpacity(1), 200);
    } else {
    }
  }, [opened]);
  useEffect(() => {
    !opened && setReplayShow(false);
  }, [opened, setReplayShow]);

  return (
    <>
      <div
        style={{
          position: 'fixed',
          display: opened ? 'block' : 'none',
          opacity: popupOpacity,
          transitionProperty: 'opacity',
          transitionDuration: '0.2s',
          transitionTimingFunction: 'ease-out',
          left: -360,
          top: -360,
          bottom: -360,
          right: -360,
          zIndex: 21,
          backgroundColor: 'black',
        }}
      >
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <div
            style={{
              position: 'absolute',
              left: 360,
              top: 360,
              bottom: 360,
              right: 360,
              padding: 0,
            }}
          >
            {uspUrl !== null && (
              <>
                {checkIfMp4(uspUrl) ? (
                  <>
                    <VideoComp
                      ref={mainRef}
                      src={uspUrl}
                      playsInline
                      autoPlay
                      style={{ width: '100%', height: '100%' }}
                      onEnded={() => setReplayShow(true)}
                    />
                    <div
                      style={{
                        position: 'fixed',
                        left: -360,
                        top: -360,
                        bottom: -360,
                        right: -360,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: replayShow ? 1 : 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        transitionProperty: 'opacity',
                        transitionDuration: '500ms',
                        zIndex: 18,
                      }}
                    >
                      <ImageComp
                        src={'/static/UI/img_replay.png'}
                        pngForced={true}
                        style={{
                          cursor: 'pointer',
                          width: isMobile ? 50 : 100,
                          height: isMobile ? 50 : 100,
                        }}
                        onClick={() => {
                          setReplayShow(false);
                          mainRef.current.play();
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <IframeComp
                    ref={mainRef}
                    src={uspUrl}
                    title="USP MODAL"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                  />
                )}
              </>
            )}
            <ImageComp
              src={'/static/UI/btn-close.png'}
              style={{
                cursor: 'pointer',
                position: 'fixed',
                zIndex: 21,
                top: 36,
                [languageInfo.rtl ? 'left' : 'right']: 36,
                width: isMobile ? 29 : 58,
                height: isMobile ? 29 : 58,
              }}
              onClick={() => {
                setPopupOpacity(0);
                setTimeout(closeFunc, 400);
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export const NavigationBar = ({ onMenuClick = () => {}, children, language }) => {
  const getResponsiveSize = useResponsiveSize();
  return (
    <>
      <div style={{ position: 'fixed', left: 0, right: 0, top: -4, zIndex: 3, pointerEvents: 'none' }}>
        <ImageComp
          src={'/static/UI/img-top-dim.png'}
          alt="top dim"
          style={{ width: '100%', height: getResponsiveSize(213, 158), objectFit: 'fill' }}
        />
      </div>
      <div
        style={{
          position: 'fixed',
          left: isMobile ? 0 : 40,
          right: isMobile ? 0 : 40,
          top: getResponsiveSize(81, 78),
          zIndex: 4,
          height: 1,
          background: '#fff',
          opacity: 0.5,
        }}
      />
      <div
        style={
          language !== null && getLanguageInfo(language).rtl
            ? {
                position: 'fixed',
                left: getResponsiveSize(65, 44),
                top: getResponsiveSize(30, 30),
                width: getResponsiveSize(29, 29),
                height: getResponsiveSize(20, 20),
                zIndex: 5,
              }
            : {
                position: 'fixed',
                right: getResponsiveSize(65, 44),
                top: getResponsiveSize(30, 30),
                width: getResponsiveSize(29, 29),
                height: getResponsiveSize(20, 20),
                zIndex: 5,
              }
        }
      >
        <ImageComp
          src={'/static/UI/btn-menu.png'}
          style={{ width: '100%', height: '100%', cursor: 'pointer' }}
          alt="menu button"
          onClick={onMenuClick}
        />
      </div>
      {children}
    </>
  );
};

export const IconButtonGroup = ({ buttons = [], language }) => {
  const getResponsiveSize = useResponsiveSize();
  return (
    <>
      {buttons.left
        .filter((ele) => ele !== null)
        .map((ele, idx) => (
          <div
            key={idx}
            style={{
              position: 'fixed',
              top: getResponsiveSize(163, 99),
              zIndex: 5,
              width: getResponsiveSize(64, 64),
              overflow: 'visible',
              [language !== null && getLanguageInfo(language).rtl ? 'right' : 'left']:
                getResponsiveSize(47, 26) + idx * getResponsiveSize(100, 92),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            onClick={ele.onClick}
          >
            <ImageComp
              src={ele.src}
              alt={ele.title}
              style={{
                width: getResponsiveSize(64, 64),
                height: getResponsiveSize(64, 64),
                marginBottom: getResponsiveSize(10, 10),
                cursor: 'pointer',
              }}
            />
            <div
              className={getFontClassName('light', language)}
              style={{ fontSize: getResponsiveSize(18, 18), color: 'white', textAlign: 'center' }}
              dangerouslySetInnerHTML={{ __html: ele.title }}
            ></div>
          </div>
        ))}
      {buttons.right
        .filter((ele) => ele !== null)
        .map((ele, idx) => (
          <div
            key={idx}
            style={{
              position: 'fixed',
              top: getResponsiveSize(163, 99),
              zIndex: 5,
              width: getResponsiveSize(64, 64),
              overflow: 'visible',
              [language !== null && getLanguageInfo(language).rtl ? 'left' : 'right']:
                getResponsiveSize(47, 26) + idx * getResponsiveSize(100, 92),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            onClick={ele.onClick}
          >
            <ImageComp
              src={ele.src}
              alt={ele.title}
              style={{
                width: getResponsiveSize(64, 64),
                height: getResponsiveSize(64, 64),
                marginBottom: getResponsiveSize(10, 10),
                cursor: 'pointer',
              }}
            />
            <div
              className={getFontClassName('light', language)}
              style={{ fontSize: getResponsiveSize(18, 18), color: 'white', textAlign: 'center' }}
              dangerouslySetInnerHTML={{ __html: ele.title }}
            ></div>
          </div>
        ))}
    </>
  );
};

export const UspIndicatorView = ({ slides = [], slideIdx = 0, setSlideIdx = (idx) => idx }) => {
  const windowWidth = useWindowWidth();
  const windowHeight = useWindowHeight();
  const getResponsiveSize = useResponsiveSize();

  return (
    <div style={{ display: 'flex' }}>
      {slides.map((ele, key) => (
        <div
          key={key}
          style={{
            paddingTop: getResponsiveSize(40, windowWidth > windowHeight ? 6 : 25),
            paddingBottom: getResponsiveSize(40, 25),
            paddingLeft: getResponsiveSize(4, 4),
            paddingRight: getResponsiveSize(4, 4),
          }}
          onClick={() => setSlideIdx(key)}
        >
          <div
            style={{
              width: getResponsiveSize(30, 30),
              height: 4,
              background: key === slideIdx ? '#011420' : '#ccd0d2',
            }}
          />
        </div>
      ))}
    </div>
  );
};

export const FeaturesFrameView = ({
  opened = false,
  slides = [],
  slideIdx = 0,
  setSlideIdx = (idx) => idx,
  closeFunc = () => {},
  languageInfo,
  carBrand = '',
  inOrEx = '',
  onTitleClick = () => {},
}) => {
  const windowWidth = useWindowWidth();
  const windowHeight = useWindowHeight();
  const getResponsiveSize = useResponsiveSize();
  useEffect(() => {
    if (!opened) {
      window.oldPosition = null;
      window.oldSlideIdx = null;
      window.newPosition = null;
    }
  }, [opened]);
  const onHandleSlide = useCallback(
    (idx) => {
      if (idx === slideIdx) {
        onTitleClick();
        gtmJourneyEventWithCustomFields(
          `select ${inOrEx} feature`,
          carBrand,
          inOrEx,
          inOrEx === 'exterior'
            ? { event_action: 'sticky menu', exterior_feature: slides[idx].title }
            : { event_action: 'sticky menu', interior_feature: slides[idx].title },
        );
      } else {
        setSlideIdx(idx);
        gtmJourneyEventWithCustomFields(
          `select ${inOrEx} feature`,
          carBrand,
          inOrEx,
          inOrEx === 'exterior' ? { exterior_feature: slides[idx].title } : { interior_feature: slides[idx].title },
        );
      }
    },
    [setSlideIdx, slideIdx, onTitleClick, carBrand, inOrEx, slides],
  );

  return (
    <>
      {opened ? (
        <>
          <ImageComp
            src={'/static/UI/btn-close.png'}
            style={{
              position: 'fixed',
              top: windowWidth > windowHeight ? getResponsiveSize(48, 18) : getResponsiveSize(48, 24),
              cursor: 'pointer',
              zIndex: 20,
              [languageInfo !== null && languageInfo.rtl ? 'left' : 'right']:
                windowWidth > windowHeight ? getResponsiveSize(48, 18) : getResponsiveSize(48, 45),
              width: getResponsiveSize(34, 28),
              height: getResponsiveSize(34, 28),
            }}
            onClick={closeFunc}
          />
          {windowWidth > windowHeight ? (
            <div
              style={{
                position: 'fixed',
                top: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                zIndex: 20,
                [languageInfo !== null && languageInfo.rtl ? 'right' : 'left']: getResponsiveSize(72, 20),
                direction: languageInfo !== null && languageInfo.rtl ? 'rtl' : 'ltr',
                color: 'white',
              }}
              onWheel={(e) => {
                if (e.deltaY > 0) {
                  setSlideIdx(slideIdx + 1 < slides.length ? slideIdx + 1 : slides.length - 1);
                } else if (e.deltaY < 0) {
                  setSlideIdx(slideIdx < 1 ? 0 : slideIdx - 1);
                }
              }}
              onTouchStart={(e) => {
                window.oldPosition = e.touches[0].clientY;
                window.oldSlideIdx = slideIdx;
                window.newPosition = e.touches[0].clientY;
              }}
              onTouchMove={(e) => {
                window.newPosition = e.touches[0].clientY;
                let deltaSlides = Math.floor(
                  (window.newPosition - window.oldPosition) / (getResponsiveSize(80, 40) + 8),
                );
                let newIdx = null;
                if (deltaSlides < 0) {
                  newIdx = window.oldSlideIdx + deltaSlides < 0 ? 0 : window.oldSlideIdx + deltaSlides;
                } else if (deltaSlides > 0) {
                  newIdx =
                    window.oldSlideIdx + deltaSlides + 1 <= slides.length
                      ? window.oldSlideIdx + deltaSlides
                      : slides.length - 1;
                }
                if (newIdx !== null && newIdx !== window.oldSlideIdx) {
                  setSlideIdx((prevIdx) => {
                    if (prevIdx !== newIdx) {
                      gtmJourneyEventWithCustomFields(
                        `select ${inOrEx} feature`,
                        carBrand,
                        inOrEx,
                        inOrEx === 'exterior'
                          ? { exterior_feature: slides[newIdx].title }
                          : { interior_feature: slides[newIdx].title },
                      );
                    }
                    return newIdx;
                  });
                }
              }}
              onTouchEnd={() => {
                window.oldPosition = null;
                window.oldSlideIdx = null;
                window.newPosition = null;
              }}
            >
              {slides.map((ele, key) => (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    flexDirection: languageInfo !== null && languageInfo.rtl ? 'row-reverse' : 'row',
                  }}
                >
                  <div
                    className={getFontClassName('medium', languageInfo.id)}
                    style={{
                      width: getResponsiveSize(56, 40),
                      fontSize: getResponsiveSize(24, 19),
                      cursor: 'pointer',
                      textShadow: '#000 0px 0 5px',
                    }}
                    onClick={() => onHandleSlide(key)}
                  >
                    {key === slideIdx ? `0${key + 1}` : ''}
                  </div>
                  <div
                    style={{
                      width: isMobile ? 4 : 2,
                      height: key === slideIdx ? getResponsiveSize(120, 96) : getResponsiveSize(80, 40),
                      marginTop: 4,
                      marginBottom: 4,
                      background: 'white',
                      opacity: key === slideIdx ? 1 : 0.5,
                      cursor: 'pointer',
                      boxShadow: '0px 0px 5px #000',
                    }}
                    onClick={() => onHandleSlide(key)}
                  />
                  <div
                    className={getFontClassName('light', languageInfo.id)}
                    style={{
                      width: getResponsiveSize(332, 283),
                      fontSize: getResponsiveSize(24, 20),
                      [languageInfo !== null && languageInfo.rtl ? 'paddingRight' : 'paddingLeft']: 18,
                      minWidth: getResponsiveSize(24, 20),
                      cursor: 'pointer',
                      textShadow: '#000 0px 0 5px',
                    }}
                    onClick={() => onHandleSlide(key)}
                    dangerouslySetInnerHTML={{ __html: slideIdx === key ? ele.title : ' ' }}
                  ></div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                position: 'fixed',
                bottom: getResponsiveSize(128, 128),
                left: getResponsiveSize(20, 20),
                right: getResponsiveSize(20, 20),
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                flexDirection: languageInfo !== null && languageInfo.rtl ? 'row-reverse' : 'row',
                zIndex: 20,
              }}
              onTouchStart={(e) => {
                window.oldPosition = e.touches[0].clientX;
                window.oldSlideIdx = slideIdx;
                window.newPosition = e.touches[0].clientX;
              }}
              onTouchMove={(e) => {
                window.newPosition = e.touches[0].clientX;
                let deltaSlides = Math.floor(
                  (window.newPosition - window.oldPosition) / (windowWidth / (slides.length + 1)),
                );
                let newIdx = null;
                if (deltaSlides < 0) {
                  newIdx = window.oldSlideIdx + deltaSlides < 0 ? 0 : window.oldSlideIdx + deltaSlides;
                } else if (deltaSlides > 0) {
                  newIdx =
                    window.oldSlideIdx + deltaSlides + 1 <= slides.length
                      ? window.oldSlideIdx + deltaSlides
                      : slides.length - 1;
                }
                if (newIdx !== null && newIdx !== window.oldSlideIdx) {
                  setSlideIdx((prevIdx) => {
                    if (prevIdx !== newIdx) {
                      gtmJourneyEventWithCustomFields(
                        `select ${inOrEx} feature`,
                        carBrand,
                        inOrEx,
                        inOrEx === 'exterior'
                          ? { exterior_feature: slides[newIdx].title }
                          : { interior_feature: slides[newIdx].title },
                      );
                    }
                    return newIdx;
                  });
                }
              }}
              onTouchEnd={() => {
                window.oldPosition = null;
                window.oldSlideIdx = null;
                window.newPosition = null;
              }}
            >
              {slides.map((ele, key) => (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    color: 'white',
                    width: slideIdx === key ? '55%' : `${(45 / (slides.length - 1)) * 0.88}%`,
                  }}
                >
                  <div
                    className={getFontClassName('medium', languageInfo.id)}
                    style={{
                      fontSize: getResponsiveSize(19, 19),
                      height: 32,
                      display: 'flex',
                      alignItems: 'flex-end',
                      cursor: 'pointer',
                      textShadow: '#000 0px 0 5px',
                    }}
                    onClick={() => onHandleSlide(key)}
                  >
                    {slideIdx === key ? `0${key + 1}` : ''}
                  </div>
                  <div
                    style={{
                      height: 4,
                      width: '100%',
                      background: 'white',
                      opacity: slideIdx === key ? 1 : 0.5,
                      cursor: 'pointer',
                      boxShadow: '0px 0px 5px #000',
                    }}
                    onClick={() => onHandleSlide(key)}
                  />
                  <div
                    className={getFontClassName('light', languageInfo.id)}
                    style={{
                      fontSize: getResponsiveSize(20, 20),
                      marginBottom: getResponsiveSize(20, 20),
                      minHeight: getResponsiveSize(20, 20),
                      cursor: 'pointer',
                      textShadow: '#000 0px 0 5px',
                    }}
                    onClick={() => onHandleSlide(key)}
                    dangerouslySetInnerHTML={{ __html: slideIdx === key ? ele.title : ' ' }}
                  ></div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </>
  );
};

export const ScreenCaptureView = ({
  opened = false,
  exOrInterior = 'exterior',
  languageInfo,
  carId,
  iframeRef,
  capturedScreen,
  setCapturedScreen,
  closeFunc = () => {},
}) => {
  const windowWidth = useWindowWidth();
  const windowHeight = useWindowHeight();
  const getResponsiveSize = useResponsiveSize();
  const [captureState, setCaptureState] = useState(0);

  return (
    <>
      {opened ? (
        <>
          {captureState === 0 ? (
            <CustomModal
              opened={true}
              closeFunc={() => {
                closeFunc();
                setCaptureState(0);
              }}
              padding={getResponsiveSize(42, 42)}
              width={getResponsiveSize(420, 420)}
            >
              <div
                className={getFontClassName('medium', languageInfo.id)}
                style={{
                  color: '#011420',
                  fontSize: getResponsiveSize(22, 23),
                  textAlign: 'center',
                  paddingTop: getResponsiveSize(20, 20),
                  paddingBottom: getResponsiveSize(44, 44),
                }}
              >
                {localization['capture_notification_txt'][languageInfo.id].replace('${car}', getCarInfo(carId).brand)}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <button
                  className={getFontClassName('medium', languageInfo.id)}
                  style={{
                    color: '#011420',
                    background: 'transparent',
                    fontSize: getResponsiveSize(18, 20),
                    border: 'solid 2px #011420',
                    width: getResponsiveSize(300, 300),
                    height: getResponsiveSize(50, 50),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => setCaptureState(1)}
                >
                  {localization['capture_notification_button'][languageInfo.id]}
                </button>
              </div>
            </CustomModal>
          ) : null}
          {captureState === 1 ? (
            <>
              <ImageComp
                src={'/static/UI/btn-close.png'}
                style={{
                  position: 'fixed',
                  top: getResponsiveSize(48, 24),
                  cursor: 'pointer',
                  zIndex: 25,
                  [languageInfo !== null && languageInfo.rtl ? 'left' : 'right']: getResponsiveSize(48, 45),
                  width: getResponsiveSize(34, 29),
                  height: getResponsiveSize(34, 29),
                }}
                onClick={() => {
                  closeFunc();
                  setCaptureState(0);
                }}
              />
              <ImageComp
                src={'/static/UI/img-camera-center.png'}
                style={{
                  position: 'fixed',
                  top: windowHeight / 2 - getResponsiveSize(40, 40),
                  left: windowWidth / 2 - getResponsiveSize(40, 40),
                  width: getResponsiveSize(80, 80),
                  height: getResponsiveSize(80, 80),
                  zIndex: 20,
                }}
              />
              <ImageComp
                src={'/static/UI/img-camera-line.png'}
                style={{
                  position: 'fixed',
                  top: windowHeight / 2 - getResponsiveSize(320, 210),
                  left: windowWidth / 2 - getResponsiveSize(590, 210),
                  width: getResponsiveSize(40, 40),
                  height: getResponsiveSize(40, 40),
                  zIndex: 20,
                }}
              />
              <ImageComp
                src={'/static/UI/img-camera-line.png'}
                style={{
                  position: 'fixed',
                  top: windowHeight / 2 - getResponsiveSize(320, 210),
                  right: windowWidth / 2 - getResponsiveSize(590, 210),
                  width: getResponsiveSize(40, 40),
                  height: getResponsiveSize(40, 40),
                  zIndex: 20,
                  transform: 'rotate(90deg)',
                }}
              />
              <ImageComp
                src={'/static/UI/img-camera-line.png'}
                style={{
                  position: 'fixed',
                  bottom: windowHeight / 2 - getResponsiveSize(320, 210),
                  left: windowWidth / 2 - getResponsiveSize(590, 210),
                  width: getResponsiveSize(40, 40),
                  height: getResponsiveSize(40, 40),
                  zIndex: 20,
                  transform: 'rotate(-90deg)',
                }}
              />
              <ImageComp
                src={'/static/UI/img-camera-line.png'}
                style={{
                  position: 'fixed',
                  bottom: windowHeight / 2 - getResponsiveSize(320, 210),
                  right: windowWidth / 2 - getResponsiveSize(590, 210),
                  width: getResponsiveSize(40, 40),
                  height: getResponsiveSize(40, 40),
                  zIndex: 20,
                  transform: 'rotate(-180deg)',
                }}
              />
              <ImageComp
                src={'/static/UI/btn-camera.png'}
                style={{
                  position: 'fixed',
                  cursor: 'pointer',
                  zIndex: 20,
                  width: getResponsiveSize(95, 95),
                  height: getResponsiveSize(95, 95),
                  [isMobile && windowWidth > windowHeight ? (languageInfo.rtl ? 'left' : 'right') : 'bottom']:
                    (isMobile && windowWidth > windowHeight ? windowWidth : windowHeight) / 2 -
                    getResponsiveSize(450, 360),
                  [isMobile && windowWidth > windowHeight ? 'top' : 'left']:
                    isMobile && windowWidth > windowHeight
                      ? windowHeight / 2 - getResponsiveSize(48, 48)
                      : windowWidth / 2 - getResponsiveSize(48, 48),
                }}
                onClick={() => {
                  setCapturedScreen(null);
                  iframeRef.current.contentWindow.postMessage(JSON.stringify({ message: 'takeScreenshot' }), '*');
                  setCaptureState(2);
                  gtmJourneyEvent('configuration options', `take ${exOrInterior} picture`, getCarInfo(carId).brand);
                }}
              />
            </>
          ) : null}
          {captureState === 2 ? (
            <>
              <div
                style={{
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  top: -360,
                  bottom: -360,
                  background: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 20,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    width: getResponsiveSize(500, 376),
                    background: 'white',
                    padding: `${getResponsiveSize(30, 30)}px ${getResponsiveSize(30, 22)}px`,
                  }}
                >
                  {capturedScreen === null ? (
                    <div
                      style={{
                        width: '100%',
                        height: getResponsiveSize(280, 210),
                        background: 'lightgrey',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      Rendering ...
                    </div>
                  ) : (
                    <img
                      src={capturedScreen}
                      alt="captured screen"
                      style={{ width: '100%', height: getResponsiveSize(280, 210), objectFit: 'cover' }}
                    />
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-around',
                      paddingTop: getResponsiveSize(30, 30),
                      opacity: capturedScreen === null ? 0.1 : 1,
                    }}
                  >
                    <button
                      style={{
                        width: getResponsiveSize(isMobile ? 220 : 440, 165),
                        height: getResponsiveSize(50, 50),
                        border: '1px solid #011420',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        fontSize: getResponsiveSize(20, 18),
                        cursor: 'pointer',
                      }}
                      disabled={capturedScreen === null}
                      onClick={() => {
                        let aEle = document.createElement('a');
                        aEle.href = capturedScreen;
                        aEle.download = 'captured.png';
                        aEle.target = '_blank';
                        document.body.appendChild(aEle);
                        aEle.click();
                        document.body.removeChild(aEle);
                        gtmJourneyEvent(
                          'configuration options',
                          `save ${exOrInterior} picture`,
                          getCarInfo(carId).brand,
                        );
                      }}
                    >
                      <ImageComp
                        src={'/static/UI/ic-save.png'}
                        alt="SAVE"
                        style={{
                          width: getResponsiveSize(22, 22),
                          height: getResponsiveSize(27, 27),
                          marginRight: getResponsiveSize(20, 15),
                        }}
                      />
                      <div className={getFontClassName('bold', languageInfo.id)}>
                        {isMobile
                          ? localization['cature_save_button'][languageInfo.id]
                          : localization['cature_download_button'][languageInfo.id]}
                      </div>
                    </button>
                    {isMobile ? (
                      <button
                        style={{
                          width: getResponsiveSize(220, 165),
                          height: getResponsiveSize(50, 50),
                          border: '1px solid #011420',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'transparent',
                          fontSize: getResponsiveSize(20, 18),
                          cursor: 'pointer',
                        }}
                        disabled={capturedScreen === null}
                        onClick={async () => {
                          if (window.navigator.canShare) {
                            try {
                              let imageFile = new File(
                                [await (await fetch(capturedScreen)).arrayBuffer()],
                                'captured.png',
                                { type: 'image/png' },
                              );
                              let shareObj = isMobileSafari
                                ? { files: [imageFile] }
                                : { title: 'Captured Image', text: 'Captured Image', files: [imageFile] };
                              if (window.navigator.canShare(shareObj)) {
                                await window.navigator.share(shareObj);
                                gtmJourneyEvent(
                                  'configuration options',
                                  `share ${exOrInterior} picture`,
                                  getCarInfo(carId).brand,
                                );
                                return;
                              } else {
                                window.alert("Your system doesn't support sharing these files.");
                                return;
                              }
                            } catch (err) {
                              console.log(err);
                              return;
                            }
                          } else {
                            window.alert("Your browser doesn't support the Web Share API.");
                            return;
                          }
                        }}
                      >
                        <ImageComp
                          src={'/static/UI/ic-shate.png'}
                          alt="SHARE"
                          style={{
                            width: getResponsiveSize(22, 22),
                            height: getResponsiveSize(27, 27),
                            marginRight: getResponsiveSize(20, 15),
                          }}
                        />
                        <div className={getFontClassName('bold', languageInfo.id)}>
                          {localization['cature_share_button'][languageInfo.id]}
                        </div>
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
              <ImageComp
                src={'/static/UI/btn-close.png'}
                style={{
                  position: 'fixed',
                  top: getResponsiveSize(48, 24),
                  cursor: 'pointer',
                  zIndex: 25,
                  [languageInfo !== null && languageInfo.rtl ? 'left' : 'right']: getResponsiveSize(48, 45),
                  width: getResponsiveSize(34, 29),
                  height: getResponsiveSize(34, 29),
                }}
                onClick={() => {
                  if (captureState === 2) {
                    setCaptureState(1);
                  } else {
                    closeFunc();
                    setCaptureState(0);
                  }
                }}
              />
            </>
          ) : null}
        </>
      ) : null}
    </>
  );
};
